/**
 * Ручной исторический (вне заказа) ввод обучения сотрудника (p2-manual-historical/IC-4,
 * technical_spec_phase2 §11.1). Роль-агностично: клиент и команда используют один и тот же
 * компонент (backend CRUD внешних записей — `POST/PATCH/DELETE /api/training/records`,
 * source='external' — уже role-agnostic, скоуп резолвится сервером по personId). Точка входа —
 * кнопка «Добавить историческое обучение» в блоке «Обучения» карточки сотрудника
 * (`employee-card.tsx`), открытие/закрытие и загрузка каталога программ — в
 * `employee-card-loader.tsx` (по образцу `startEdit`/`reloadToken`).
 *
 * Поля §11.1: сотрудник — из контекста (`personId`, не вводится); направление (select);
 * программа (select, отфильтрован по направлению — тем самым и «сгруппирован» по нему, §4);
 * дата обучения; дата истечения — READ-ONLY (вычисляется сервером по периодичности программы,
 * здесь — предпросмотр); otIndices — чипы А/Б/СИЗ/ПП/В, только при direction='ot'. БЕЗ поля
 * «комментарий» (реш. 5 плана фазы 2 — `CreateExternalRecordInput` его не содержит).
 *
 * Правка внесённой записи — та же форма с `record`: поля заполнены её значениями, сохранение идёт
 * в `PATCH /api/training/records/{id}`. Править можно только историческую запись (source=
 * 'external'); запись из заказа сервер менять не даёт.
 */
import { useMemo, useState } from 'react'
import { trainingApi, type Program, type TrainingRecord } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { isNotFutureDate } from '@/lib/date-order'
import { computeDueOn, OT_INDICES, type OtIndex } from '@/lib/training'
import { programLine } from '@/lib/training-catalog'
import { formatHistoricalDate } from '@/sout/historical-sout-form'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { cn } from '@/lib/utils'

interface HistoricalTrainingDraft {
  /** id направления из справочника (не системный код) — centrika-docs!55. */
  direction: string
  programId: string
  lastTrainedOn: string
  otIndices: OtIndex[]
}

function emptyDraft(): HistoricalTrainingDraft {
  return { direction: '', programId: '', lastTrainedOn: '', otIndices: [] }
}

/** Черновик из внесённой записи; направление берётся из каталога по её программе. */
function draftFromRecord(record: TrainingRecord, programs: Program[]): HistoricalTrainingDraft {
  return {
    direction: programs.find((p) => p.id === record.programId)?.directionId ?? '',
    programId: record.programId,
    lastTrainedOn: record.lastTrainedOn ?? '',
    otIndices: OT_INDICES.filter((idx) => record.otIndices.includes(idx)),
  }
}

export interface HistoricalTrainingFormProps {
  /** Сотрудник, для которого вводится запись — из контекста карточки, не поле формы. */
  personId: string
  /** Каталог программ (уже загружен вызывающей стороной, `trainingApi.listPrograms`). */
  programs: Program[]
  /** Правка внесённой исторической записи; без неё форма создаёт новую. */
  record?: TrainingRecord
  onSaved: () => void
  onCancel: () => void
}

export function HistoricalTrainingForm({
  personId,
  programs,
  record,
  onSaved,
  onCancel,
}: HistoricalTrainingFormProps) {
  const [draft, setDraft] = useState<HistoricalTrainingDraft>(() =>
    record ? draftFromRecord(record, programs) : emptyDraft(),
  )
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  // Поиск программы по коду и названию во всех направлениях (training-followups): в каталоге сотни
  // программ — найденная сама выставляет направление и программу.
  const [search, setSearch] = useState('')

  // Архивные и неподтверждённые программы в выбор не попадают (training-catalog-v2,
  // training-packages-approval; неподтверждённые приходят только команде) — кроме программы самой
  // правимой записи: её запись по-прежнему можно поправить.
  const selectable = useMemo(
    () => programs.filter((p) => (!p.archived && p.status === 'active') || p.id === record?.programId),
    [programs, record?.programId],
  )

  // Направления берём из справочника через сами программы (актуальные имена, включая
  // пользовательские) — а не из статичного списка системных кодов (centrika-docs!55).
  const directionOptions = useMemo(() => {
    const byId = new Map<string, string>()
    for (const p of selectable) if (!byId.has(p.directionId)) byId.set(p.directionId, p.directionName)
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name, 'ru'),
    )
  }, [selectable])

  const filteredPrograms = draft.direction
    ? selectable.filter((p) => p.directionId === draft.direction)
    : []
  const selectedProgram = programs.find((p) => p.id === draft.programId)
  // Системный код выбранного направления (для чипов ОТ) — из любой его программы.
  const selectedDirectionSystemCode =
    programs.find((p) => p.directionId === draft.direction)?.directionSystemCode ?? ''
  const isOtDirection = selectedDirectionSystemCode === 'ot'
  // У программ ОТ из каталога индекс задан самой программой — чипы не нужны (training-catalog-v2).
  const programOtIndex = selectedProgram?.otIndex ?? null

  const searchMatches = useMemo(() => {
    const needle = search.trim().toLowerCase().replaceAll('ё', 'е')
    if (needle.length < 2) return []
    return selectable
      .filter((p) =>
        [p.code, p.shortName, p.name].some((v) => (v ?? '').toLowerCase().replaceAll('ё', 'е').includes(needle)),
      )
      .slice(0, 10)
  }, [search, selectable])

  function pickProgram(p: Program) {
    setDraft((d) => ({
      ...d,
      direction: p.directionId,
      programId: p.id,
      otIndices: p.directionSystemCode === 'ot' ? d.otIndices : [],
    }))
    setSearch('')
  }

  function setDirection(directionId: string) {
    const sys = programs.find((p) => p.directionId === directionId)?.directionSystemCode ?? ''
    setDraft((d) => ({
      ...d,
      direction: directionId,
      // Программа/индексы теряют смысл при смене направления — сбрасываем.
      programId: '',
      otIndices: sys === 'ot' ? d.otIndices : [],
    }))
  }

  function toggleOtIndex(idx: OtIndex) {
    setDraft((d) => ({
      ...d,
      otIndices: d.otIndices.includes(idx) ? d.otIndices.filter((i) => i !== idx) : [...d.otIndices, idx],
    }))
  }

  const validUntilPreview = (() => {
    if (!selectedProgram || !draft.lastTrainedOn) return '—'
    // centrika-docs!54: режим «определяет работодатель» — дата не рассчитывается.
    if (selectedProgram.recurrenceMode === 'employer') return 'Определяет работодатель'
    if (selectedProgram.recurrenceMonths == null) return 'бессрочно'
    const due = computeDueOn(draft.lastTrainedOn, selectedProgram.recurrenceMonths)
    return due ? formatHistoricalDate(due) : '—'
  })()

  // Историческое обучение уже состоялось: дата в будущем — ошибка ввода (обычно в это поле
  // попадает дата окончания срока), запись при этом выглядела бы действующей ещё несколько лет.
  const trainedInFuture = !isNotFutureDate(draft.lastTrainedOn)

  const canSave =
    Boolean(draft.direction && draft.programId && draft.lastTrainedOn) && !trainedInFuture && !saving

  async function save() {
    if (!draft.direction || !draft.programId || !draft.lastTrainedOn || trainedInFuture) return
    setSaveError(null)
    setSaving(true)
    try {
      if (record) {
        // Индексы передаются всегда: при смене направления с «Охраны труда» их нужно очистить.
        await trainingApi.updateRecord(record.id, {
          programId: draft.programId,
          lastTrainedOn: draft.lastTrainedOn,
          otIndices: isOtDirection ? draft.otIndices : [],
        })
      } else {
        await trainingApi.createRecord({
          personId,
          programId: draft.programId,
          lastTrainedOn: draft.lastTrainedOn,
          otIndices: isOtDirection ? draft.otIndices : undefined,
        })
      }
      onSaved()
    } catch (err) {
      setSaveError(getErrorMessage(err, 'Не удалось сохранить историческую запись'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-3 rounded-8 border border-line p-3">
      {saveError ? (
        <Alert variant="danger" className="mb-2">
          {saveError}
        </Alert>
      ) : null}
      <Field label="Найти программу" hint="Код или название — направление и программа подставятся сами">
        {(props) => (
          <Input
            {...props}
            value={search}
            placeholder="Например: пожарная безопасность"
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
      </Field>
      {searchMatches.length > 0 ? (
        <ul className="mb-3 flex flex-col gap-1" aria-label="Найденные программы">
          {searchMatches.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="w-full rounded-8 px-2 py-1 text-left text-13 text-txt hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                onClick={() => pickProgram(p)}
              >
                {programLine(p)}
                <span className="ml-1 text-txt-tertiary">· {p.directionName}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : search.trim().length >= 2 ? (
        <p className="mb-3 text-13 text-txt-secondary">Ничего не найдено — выберите направление и программу ниже.</p>
      ) : null}
      <Field label="Направление" required>
        {(props) => (
          <Select value={draft.direction} onValueChange={setDirection}>
            <SelectTrigger
              id={props.id}
              aria-label="Направление"
              aria-required={props['aria-required']}
            >
              <SelectValue placeholder="Выберите направление" />
            </SelectTrigger>
            <SelectContent>
              {directionOptions.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>
      <Field
        label="Программа"
        required
        hint={draft.direction ? undefined : 'Сначала выберите направление'}
      >
        {(props) => (
          <Select
            value={draft.programId}
            onValueChange={(v) => setDraft((d) => ({ ...d, programId: v }))}
            disabled={!draft.direction}
          >
            <SelectTrigger
              id={props.id}
              aria-label="Программа"
              aria-required={props['aria-required']}
            >
              <SelectValue placeholder="Выберите программу" />
            </SelectTrigger>
            <SelectContent>
              {filteredPrograms.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>
      <Field
        label="Дата обучения"
        required
        error={trainedInFuture ? 'Дата обучения не может быть в будущем' : undefined}
      >
        {(props) => (
          <DateInput
            {...props}
            invalid={props['aria-invalid'] || trainedInFuture}
            value={draft.lastTrainedOn}
            onChange={(lastTrainedOn) => setDraft((d) => ({ ...d, lastTrainedOn }))}
          />
        )}
      </Field>
      <Field label="Дата истечения" hint="Вычисляется автоматически — не вводится вручную">
        {(props) => <Input {...props} readOnly disabled value={validUntilPreview} />}
      </Field>
      {isOtDirection && programOtIndex ? (
        <Field label="Индекс ОТ">
          {(props) => <Input {...props} readOnly disabled value={programOtIndex} />}
        </Field>
      ) : isOtDirection ? (
        <Field label="Индексы ОТ" hint="Необязательно — применимые индексы охраны труда">
          {({ id }) => (
            <div id={id} role="group" aria-label="Индексы ОТ" className="flex flex-wrap gap-1.5">
              {OT_INDICES.map((idx) => {
                const active = draft.otIndices.includes(idx)
                return (
                  <button
                    key={idx}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleOtIndex(idx)}
                    className={cn(
                      'rounded-pill border px-2 py-1 text-12 transition-colors',
                      active
                        ? 'border-accent bg-accent-bg text-accent-txt'
                        : 'border-line-strong bg-white text-txt-secondary hover:bg-hover',
                    )}
                  >
                    {idx}
                  </button>
                )
              })}
            </div>
          )}
        </Field>
      ) : null}
      <div className="mt-2 flex gap-2">
        <Button disabled={!canSave} onClick={() => void save()}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </div>
  )
}
